import { useMemo } from 'react';

import type { TableProps } from 'antd';
import { Card, Spin, Table, Tooltip, Typography } from 'antd';

import MyIcon from 'components/Icon';
import Truncate from 'components/Truncate';
import { PRODUCT_STATUS_LABEL } from 'helpers/enums';
import ProductStatusTag from 'scenes/Producer/Product/MyProducts/components/ProductStatusTag';
import { TableActionButton } from 'v2/components/Shared/TableActionButton';
import { useProductVariations } from 'v2/hooks/useProductVariations';
import type { IProduct } from 'v2/interfaces/product';

import ShowPrice from '../ShowPrice';

type DataChildrenProps = {
  product: IProduct;
  onClickOptions: (product: IProduct, activatedChildren: number) => void;
};

const DataChildren = ({ product, onClickOptions }: DataChildrenProps) => {
  const productVariationsQuery = useProductVariations(product.id, {
    enabled: true
  });

  const columns = useMemo(
    (): TableProps['columns'] => [
      {
        key: 'product',
        title: 'Produto',
        render: (_: unknown, variation: any) => (
          <div className='flex items-center gap-3'>
            <div className='border-outline-default bg-surface-subtle relative size-10 shrink-0 overflow-hidden rounded-sm border'>
              {variation.image ? (
                <img src={variation.image} alt='' className='absolute inset-0 size-full object-cover' />
              ) : null}
            </div>
            <div className='min-w-0 overflow-hidden'>
              <div className='flex items-center gap-1.5' title={variation.title || 'Desconhecido'}>
                <Typography.Text strong ellipsis>
                  <Truncate count={80} final='...'>
                    {variation.title || 'Desconhecido'}
                  </Truncate>
                </Typography.Text>
                {variation.charge_type === 'A' && (
                  <Tooltip title='Recorrência' placement='top'>
                    <div className='w-3.75 shrink-0'>
                      <MyIcon className='icon' name='icon-assinaturas' />
                    </div>
                  </Tooltip>
                )}
              </div>
              <Typography.Text type='secondary'>{variation.id}</Typography.Text>
            </div>
          </div>
        )
      },
      {
        key: 'moderation',
        title: 'Moderação',
        width: 144,
        render: (_: unknown, variation: any) => (
          <ProductStatusTag status={variation.moderation_status} productType={variation.type} />
        )
      },
      {
        key: 'status',
        title: 'Status',
        width: 112,
        render: (_: unknown, variation: any) => (
          <Typography.Text>{PRODUCT_STATUS_LABEL[variation.status]}</Typography.Text>
        )
      },
      {
        key: 'price',
        title: 'Preço',
        width: 200,
        render: (_: unknown, variation: any) => <ShowPrice product={variation} commission='normal' />
      },
      {
        key: 'actions',
        title: 'Ações',
        width: 64,
        align: 'right',
        render: (_: unknown, variation: any) => (
          <TableActionButton onClick={() => onClickOptions(variation, productVariationsQuery.data?.length)} />
        )
      }
    ],
    [onClickOptions, productVariationsQuery.data?.length]
  );

  if (productVariationsQuery.isLoading) {
    return (
      <Card size='small' className='overflow-hidden'>
        <div className='flex justify-center py-6'>
          <Spin size='small' />
        </div>
      </Card>
    );
  }

  return (
    <Card size='small' className='overflow-hidden' classNames={{ body: 'p-0' }}>
      <Table
        size='small'
        bordered={false}
        dataSource={productVariationsQuery.data}
        columns={columns}
        rowKey='id'
        pagination={false}
      />
      <span hidden aria-hidden />
    </Card>
  );
};

export default DataChildren;
